import React, { useEffect, createContext, useContext, useRef } from 'react';
import { usePayments } from '../hooks/usePayments';
import { PaymentsConfig } from '../../shared/types/config';
import { PaymentEvent } from '../../shared/types/events';
import { PaymentError } from '../../shared/types/errors';

import { Customer, CustomerCreateParams } from '../../shared/types/customer';
import {
  Subscription,
  SubscriptionUpdateParams,
  SubscriptionStatus,
} from '../../shared/types/subscription';
import { CheckoutParams, CheckoutSession } from '../../shared/types/checkout';

/**
 * Native-specific PaymentsProvider
 * This provider is designed for React Native applications and uses the native-specific usePayments hook
 * which makes server-side API calls instead of directly using the Node.js Stripe SDK
 */

interface PaymentsContextType {
  config: PaymentsConfig | null;
  initialized: boolean;
  loading: boolean;
  error: PaymentError | null;
  customer: Customer | null;
  subscriptions: Subscription[];
  activeSubscription: Subscription | null;
  createCustomer: (params: CustomerCreateParams) => Promise<Customer>;
  createCheckoutSession: (params: CheckoutParams) => Promise<CheckoutSession>;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  reactivateSubscription: (subscriptionId: string) => Promise<void>;
  updateSubscription: (
    subscriptionId: string,
    params: SubscriptionUpdateParams
  ) => Promise<Subscription>;
  refreshCustomer: (customerId: string) => Promise<void>;
  refreshSubscriptions: (
    customerId: string,
    status?: SubscriptionStatus
  ) => Promise<void>;
  reset: () => void;
}

const PaymentsContext = createContext<PaymentsContextType | undefined>(
  undefined
);

interface PaymentsProviderProps {
  children: React.ReactNode;
  config?: PaymentsConfig;
  onPaymentSuccess?: (event: PaymentEvent) => void;
  onPaymentError?: (error: PaymentError) => void;
}

export const PaymentsProvider: React.FC<PaymentsProviderProps> = ({
  children,
  config,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const store = usePayments();
  const {
    initialize,
    initialized,
    loading,
    error,
    customer,
    subscriptions,
    activeSubscription,
  } = store;

  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  const onPaymentErrorRef = useRef(onPaymentError);

  useEffect(() => {
    onPaymentSuccessRef.current = onPaymentSuccess;
  }, [onPaymentSuccess]);

  useEffect(() => {
    onPaymentErrorRef.current = onPaymentError;
  }, [onPaymentError]);

  useEffect(() => {
    if (!initialized && !loading && !error && config) {
      initialize(config).catch(err => {
        console.error('PaymentsProvider initialization failed:', err);
        if (onPaymentErrorRef.current) {
          onPaymentErrorRef.current(err);
        }
      });
    }
  }, [initialized, loading, error, config, initialize]);

  const contextValue: PaymentsContextType = {
    config: store.config,
    initialized,
    loading,
    error,
    customer,
    subscriptions,
    activeSubscription,
    createCustomer: store.createCustomer,
    createCheckoutSession: store.createCheckoutSession,
    cancelSubscription: store.cancelSubscription,
    reactivateSubscription: store.reactivateSubscription,
    updateSubscription: store.updateSubscription,
    refreshCustomer: store.refreshCustomer,
    refreshSubscriptions: store.refreshSubscriptions,
    reset: store.reset,
  };

  return (
    <PaymentsContext.Provider value={contextValue}>
      {children}
    </PaymentsContext.Provider>
  );
};

export const usePaymentsContext = () => {
  const context = useContext(PaymentsContext);
  if (context === undefined) {
    throw new Error(
      'usePaymentsContext must be used within a PaymentsProvider'
    );
  }
  return context;
};
